import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { GALLERY } from './data';

export const Gallery = () => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setActive(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <section id="galeria" data-testid="gallery-section" className="py-20 sm:py-32 border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <p className="text-acid uppercase tracking-[0.3em] text-xs mb-4">En directo</p>
          <h2 className="font-heading uppercase text-4xl sm:text-6xl tracking-tight text-[#f1f0ea]">
            La <span className="text-acid">galería</span>
          </h2>
          <p className="mt-5 text-[#f1f0ea]/70 font-light max-w-2xl">
            Momentos reales a los platos. Toca cualquier imagen para verla en grande.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
          {GALLERY.map((img, i) => (
            <motion.button
              key={img.src}
              data-testid={`gallery-item-${i}`}
              onClick={() => setActive(img)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
              className="group relative overflow-hidden border border-white/10 hover:border-acid transition-colors aspect-square"
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover saturate-[0.85] group-hover:scale-105 group-hover:saturate-100 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 text-[#f1f0ea] hover:text-acid transition-colors"
              aria-label="Cerrar"
            >
              <X size={34} />
            </button>
            <motion.img
              key={active.src}
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.3 }}
              src={active.src}
              alt={active.alt}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-full object-contain border border-white/10"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
